import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import EllipsisLoader from "../components/EllipsisLoader";
import Icon from "../components/Icon";
import StyleHeroBanner from "../components/StyleHeroBanner";
import StyleThumbnails from "../components/StyleThumbnails";
import Tabs from "../components/Tabs";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { IconName, InteriorStyle, UpgradeStyle } from "../types";
import { scrollToTop } from "../util";
import Rendivo from "../util/rendivo";

const INTERIOR_TAB_TITLE = "Interior Colour Scheme";
const KITCHEN_TAB_TITLE = "Cabinetry Colours";
const FLOORING_TAB_TITLE = "Main Flooring";

const ChooseInteriorStyleScreen = observer(() => {
  const userContext = useContext(UserContext);
  const apiContext = useContext(ApiContext);
  const navContext = useContext(NavigationContext);
  const windowSize = useWindowSize();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [tabTitles, setTabTitles] = useState([INTERIOR_TAB_TITLE]);

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    scrollToTop();
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedInteriorStyle(null);
      userContext.resetSelectedFlooringStyle();
    });

    // Select default option
    if (userContext.selectedInteriorStyle) {
      onClickInteriorColour(userContext.selectedInteriorStyle);
    } else if (apiContext.exteriorStyleResults.length) {
      onClickInteriorColour(apiContext.interiorStyleResults[0]);
    }
    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
    if (tabIndex < tabTitles.length - 1) {
      navContext.setNextBtnOnClick(() => {
        onChangeTabIndex(tabIndex + 1);
      });
    }
    if (tabIndex > 0) {
      navContext.setBackBtnOnClick(() => {
        setTabIndex(tabIndex - 1);
      });
    }
  }, [tabIndex, tabTitles]);

  useEffect(() => {
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.subtotalPrice]);

  const onClickInteriorColour = (interiorStyle: InteriorStyle) => {
    // If the interior style changes, we need to try and keep the user's kitchen and flooring selections
    let kitchenStyle = null;
    let flooringStyle = null;
    if (
      userContext.selectedInteriorStyle &&
      userContext.selectedInteriorStyle.id !== interiorStyle.id
    ) {
      const currentKitchenStyle = userContext.selectedKitchenStyle.get(
        userContext.selectedInteriorStyle.id
      );
      const currentFlooringStyle = userContext.selectedFlooringStyle.get(
        userContext.selectedInteriorStyle.id
      );

      if (currentKitchenStyle) {
        const matchingKitchenStyle = interiorStyle.kitchen_options.find(
          (item) =>
            item.title.toLowerCase() === currentKitchenStyle.title.toLowerCase()
        );
        if (matchingKitchenStyle) {
          kitchenStyle = matchingKitchenStyle;
        }
      }

      if (currentFlooringStyle) {
        const matchingFlooringStyle = interiorStyle.flooring_options.find(
          (item) =>
            item.title.toLowerCase() ===
            currentFlooringStyle.title.toLowerCase()
        );
        if (matchingFlooringStyle) {
          flooringStyle = matchingFlooringStyle;
        }
      }
    }
    if (!kitchenStyle && interiorStyle.kitchen_options.length > 0) {
      kitchenStyle = interiorStyle.kitchen_options[0];
    }
    if (kitchenStyle) {
      userContext.setSelectedKitchenStyle(interiorStyle.id, kitchenStyle);
    }

    if (!flooringStyle && interiorStyle.flooring_options.length > 0) {
      flooringStyle = interiorStyle.flooring_options[0];
    }
    if (flooringStyle) {
      userContext.setSelectedFlooringStyle(interiorStyle.id, flooringStyle);
    }

    userContext.setSelectedInteriorStyle(interiorStyle);

    const newTabTitles = [INTERIOR_TAB_TITLE];
    if (interiorStyle.kitchen_options.length > 0) {
      newTabTitles.push(KITCHEN_TAB_TITLE);
    }
    if (interiorStyle.flooring_options.length > 0) {
      newTabTitles.push(FLOORING_TAB_TITLE);
    }
    setTabTitles(newTabTitles);
  };

  const onChangeTabIndex = (index: number) => {
    if (index !== tabIndex) {
      setSlideIndex(0);
      setTabIndex(index);
    }
  };

  const getSliderItems = () => {
    if (userContext.selectedInteriorStyle) {
      if (tabTitles[tabIndex] === INTERIOR_TAB_TITLE) {
        // Colour Scheme tab shows all renders
        return userContext.selectedInteriorStyle.slider_items;
      }

      if (tabTitles[tabIndex] === KITCHEN_TAB_TITLE) {
        // Kitchen Tab shows only shows Kitchen and Bathroom
        return userContext.selectedInteriorStyle.slider_items.filter(
          (item) => item.type !== "bedroom"
        );
      }

      if (tabTitles[tabIndex] === FLOORING_TAB_TITLE) {
        // Flooring Tab shows only shows Kitchen and Bedroom
        return userContext.selectedInteriorStyle.slider_items.filter(
          (item) => item.type !== "bathroom"
        );
      }
    }

    return [];
  };

  const getTagline = () => {
    const sliderItems = getSliderItems();
    if (userContext.selectedInteriorStyle && sliderItems.length >= slideIndex) {
      return sliderItems[slideIndex].title;
    }

    return "";
  };

  const getInteriorRenders = () => {
    const selectedInterior = userContext.selectedInteriorStyle;

    if (selectedInterior) {
      const selectedKitchenStyle = userContext.selectedKitchenStyle.get(
        selectedInterior.id
      );
      const selectedFlooringStyle = userContext.selectedFlooringStyle.get(
        selectedInterior.id
      );
      return getSliderItems()
        .filter((item) => item.rendivo_id !== null || item.image !== null)
        .map((item) => {
          if (item.rendivo_id !== null) {
            // Inclusions can override the kitchen image, need to support that here.
            let rendivoProductId = item.rendivo_id;
            let kitchenId = "";
            let flooringId = "";
            if (
              item.type === "kitchen" &&
              userContext.selectedInclusion &&
              userContext.selectedInclusion.kitchen_rendivo_id
            ) {
              rendivoProductId =
                userContext.selectedInclusion.kitchen_rendivo_id;
            }

            if (selectedKitchenStyle) {
              kitchenId = selectedKitchenStyle.rendivo_id;
            }
            if (selectedFlooringStyle) {
              flooringId = selectedFlooringStyle.rendivo_id;
            }

            return Rendivo.getExteriorRender(
              1640,
              rendivoProductId,
              null,
              selectedInterior.rendivo_id,
              kitchenId,
              flooringId,
              "",
              "",
              false
            );
          }else if(item.image !== null){
            return item.image;
          }else{
            return "";
          }
        });
    }

    return [];
  };

  const renderTabContent = (tabIndex: number) => {
    const selectedInterior = userContext.selectedInteriorStyle;
    if (!selectedInterior) {
      return <></>;
    }

    return (
      <div className={"tab-content"}>
        <StyleHeroBanner
          showRendivoDisclaimer={apiContext.rendivoDisclaimerVisible}
          onSlide={(index) => setSlideIndex(index)}
          images={getInteriorRenders()}
          tagline={windowSize.width > 768 ? getTagline() : undefined}
          currentSlideIndex={slideIndex}
          usingRendivoImages={false}
        />
      </div>
    );
  };

  return (
    <div className={"screen screen-choose-interior-style"}>
      <div className={"container"}>
        <h2 className="h1"><strong className="number">Step 4:</strong> {getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          Choose an interior colour scheme you’ll love to live in each and every
          day. Inspired by the natural landscape.
        </p>
      </div>
      <div className="flex">
        <div className="flex-left">
          {renderTabContent(1)}
        </div>
        <div className="flex-right">
          <div className="thumbnails-nav">
            <span className="instructions">Choose Your Interior Colour Scheme</span>
            <div className="nav-right">
              <span>{apiContext.interiorStyleResults.length} Options</span>
            </div>
          </div>
          <p className="more-info">Selection applies to all Kitchen, Family Bathroom and Laundry spaces.</p>
          <div className={"thumbnails"}>
            {apiContext.interiorStyleResults.map((interior) => {
              return (
                <div
                  key={interior.id}
                  className={`thumbnail ${userContext.selectedInteriorStyle && userContext.selectedInteriorStyle.id === interior.id ? "active" : ""}`} 
                  onClick={() => onClickInteriorColour(interior)}
                >
                  <div className="thumbnail-image">
                    <EllipsisLoader />
                    <img src={interior.thumbnail} alt={""} />
                  </div>
                  <div className="thumbnail-text">{ interior.title }</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ChooseInteriorStyleScreen;
